import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Polls /student/notifications/summary so the mobile "More" tab can render a
 * red unread dot when admins reply to support tickets or new badges unlock.
 *
 * Polling interval: 60s while the tab is foreground. Stops polling when the
 * document is hidden to avoid wasting battery on mobile.
 */
export function useStudentNotifications({ pollMs = 60_000, enabled = true } = {}) {
  const [summary, setSummary] = useState({ unread_badges: 0, unread_support_replies: 0, has_unread: false });
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/student/notifications/summary");
      setSummary(r.data || { unread_badges: 0, unread_support_replies: 0, has_unread: false });
    } catch {
      // Silent — this is a non-critical badge indicator.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(refresh, pollMs);
    };
    const stop = () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    const onVisibility = () => {
      if (document.hidden) { stop(); }
      else { refresh(); start(); }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, pollMs, refresh]);

  /** Mark a source as seen and optimistically clear it locally. */
  const markSeen = useCallback(async (source) => {
    if (source !== "badges" && source !== "support") return;
    setSummary((s) => ({
      unread_badges: source === "badges" ? 0 : s.unread_badges,
      unread_support_replies: source === "support" ? 0 : s.unread_support_replies,
      has_unread: source === "badges"
        ? s.unread_support_replies > 0
        : s.unread_badges > 0,
    }));
    try { await api.post("/student/notifications/mark-seen", { source }); }
    catch { /* will retry on next poll */ }
  }, []);

  return { ...summary, refresh, markSeen };
}
